// © Microsoft Corporation. All rights reserved.

import { isAppCache } from "./Utilities";

// todo: Deliverable 29531401: [Client] Move client-data-settings to a separate library to avoid importing core library
// Import common functions from the new library once the above deliverable is completed.

let clientSettings: ClientSettings;
let currentFlights: string;

/** The client settings object. */
export interface ClientSettings {
    /** The name of the app */
    apptype: string;

    /** The name of the page */
    pagetype: string;

    /** The activity ID */
    aid: string;

    /** The PCS page generation time in ISO 8601 format, UTC. */
    pageGenTime?: string;

    /** Information about the JS bundles we are running. */
    bundleInfo: {
        /** The build number for the JS bundles. */
        v: string;
    }

    /** Information about PCS that created the page. */
    pcsInfo: {
        /** The environment we are on */
        env: string;

        /** The build number for PCS. */
        v: string;
    }
}

/**
 * Gets the client settings object.
 */
export function getPageSettings(): ClientSettings {
    if (!clientSettings) {
        const settingsJson: string = document.head.getAttribute("data-client-settings");
        if (settingsJson) {
            clientSettings = JSON.parse(settingsJson);
        }
    }

    return clientSettings;
}

/**
 * Extract eligible flight ids from head data.
 * @returns string of flight ids.
 */
export function getCurrentFlights(): string {
    if (currentFlights) {
        return currentFlights;
    }

    let clientInfo = document.head.getAttribute("data-info");
    const clientSettings = getPageSettings();

    // If page is possibly app cached, check the updated head data if available.
    // Use flights from stored head data if they aren't too old.
    // This logic is similiar to initializeFromStorage from HeadData
    // except we have to do it first in the inline script to get the flight ids.
    if (isAppCache()) {
        let storedHeadData;
        try {
            const storedHeadDataString = window.localStorage.getItem("shd_" + clientSettings.pagetype);
            storedHeadData = JSON.parse(storedHeadDataString);
        } catch {
            storedHeadData = null;
        }

        if (storedHeadData) {
            const responseClientSettings = storedHeadData.clientSettings;
            const responseFlights = storedHeadData.info;

            // The stored head data is corrupt, bail
            if (responseClientSettings && responseFlights) {

                // If DOM page gen time is later than response page gen time, bail. We don't want to load stale head data from storage.
                if (clientSettings.pageGenTime < responseClientSettings.pageGenTime) {
                    // Use the new flights from stored head data
                    clientInfo = responseFlights;
                }
            }
        }
    }

    // Create array from comma separated list of flights enabled in current request.
    currentFlights = ((/f:\s*([^;]+)/i.exec(clientInfo) || {})[1] || "").toLowerCase();

    return currentFlights;
}